import React, { useMemo, useState, useEffect } from 'react';
import { Checkbox, Input, Button, Typography } from '../../../../../../../components';
import { StorageCriteria } from '../../../DiscoveryInventoryTable/components/StorageAndMemoryCriteria';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { returnTotalDiskCriteria } from '../../../../../helpers';
import { parseDiskData, getGiBfromMiB, getMiBFromGiB } from '../../../../../../../lib/helpers';
import { OPTIMIZATION_TYPES, REQUIRED_OPTIONS, DEFAULT_NEW_DISK_SIZE_GB } from '../../constants';

const { Title, Text } = Typography;

export const StorageSettings = ({
    record,
    ignoredOptimizations,
    setIgnoredOptimizations,
    additionalOptimizations,
    setAdditionalOptimizations,
    isLoading,
    scrollToBottom,
}) => {
    const storageIgnored = ignoredOptimizations?.storage || [];

    // Check if storage optimizations are ignored
    const isIgnoreOptimizationsChecked = useMemo(
        () =>
            storageIgnored.includes(OPTIMIZATION_TYPES.UNDERSIZED) &&
            storageIgnored.includes(OPTIMIZATION_TYPES.OVERSIZED),
        [ignoredOptimizations],
    );

    const [disks, setDisks] = useState([]);

    useEffect(() => {
        // Merge original disks with any optimized sizes from additionalOptimizations
        const originalDisks = parseDiskData(record?.disks) || [];
        const optimizedDisks = additionalOptimizations?.optimizations?.storage?.disks || {};
        const optimizedIndices = Object.keys(optimizedDisks).map((key) => parseInt(key, 10));
        // Safety check if there are no original disks and no optimized disks
        if (originalDisks.length === 0 && optimizedIndices.length === 0) {
            setDisks([]);
            return;
        }

        const maxIndex = Math.max(originalDisks.length - 1, ...optimizedIndices);
        const mergedDisks = [];
        for (let i = 0; i <= maxIndex; i++) {
            if (i < originalDisks.length) {
                // Use original disk, update capacity if optimized
                mergedDisks.push({
                    ...originalDisks[i],
                    capacity: optimizedDisks[i] !== undefined ? optimizedDisks[i] : originalDisks[i].capacity,
                });
            } else if (optimizedDisks[i] !== undefined) {
                mergedDisks.push({
                    name: i,
                    capacity: optimizedDisks[i],
                });
            }
        }
        setDisks(mergedDisks);
    }, [record, additionalOptimizations]);

    // Calculate total and used storage
    const { criteria, totalSpace, totalUsed } = returnTotalDiskCriteria(record);

    const originalDiskCount = useMemo(() => {
        return (parseDiskData(record?.disks) || []).length;
    }, [record]);

    const handleDeleteDisk = (index) => {
        const updatedDisks = disks.filter((_, i) => i !== index);
        setDisks(updatedDisks);
        setAdditionalOptimizations((prev) => {
            // Rebuild disks object from scratch every time to ensure indices are correct after deletion
            const prevDisksObj = prev.optimizations.storage.disks;
            const newDisksObj = {};
            // Add original disks that are not deleted
            for (let i = 0; i < originalDiskCount; i++) {
                if (prevDisksObj[i]) {
                    newDisksObj[i] = prevDisksObj[i];
                }
            }
            // Add optimized disks that were not deleted
            updatedDisks.slice(originalDiskCount).forEach((disk, i) => {
                newDisksObj[originalDiskCount + i] = disk.capacity;
            });

            return {
                ...prev,
                optimizations: {
                    ...prev.optimizations,
                    storage: {
                        ...prev.optimizations.storage,
                        disks: newDisksObj,
                    },
                },
            };
        });
    };

    const handleDiskSizeChange = (index, value) => {
        const newCapacityMiB = getMiBFromGiB(value);
        const updatedDisks = disks.map((disk, i) => (i === index ? { ...disk, capacity: newCapacityMiB } : disk));
        // Update the optimizations object so we can send it in the request
        setAdditionalOptimizations((prev) => ({
            ...prev,
            optimizations: {
                ...prev.optimizations,
                storage: {
                    ...prev.optimizations.storage,
                    disks: { ...prev.optimizations.storage.disks, [index]: newCapacityMiB },
                },
            },
        }));
        setDisks(updatedDisks);
        scrollToBottom();
    };

    // Update disk size if below min size when user clicks away from the input
    const handleDiskSizeBlur = (index) => {
        // Min size is the used space if VM is powered on, otherwise use the total allocated storage
        const minSize = record?.total_free_space
            ? record?.total_allocated_storage - record?.total_free_space
            : record?.total_allocated_storage;
        const currentSize = disks[index].capacity;

        // If the size they specified is less than the min size, update it to the min size
        if (currentSize < minSize || isNaN(currentSize)) {
            const updatedDisks = disks.map((disk, i) => {
                return i === index ? { ...disk, capacity: minSize } : disk;
            });
            setDisks(updatedDisks);

            setAdditionalOptimizations((prev) => ({
                ...prev,
                optimizations: {
                    ...prev.optimizations,
                    storage: {
                        ...prev.optimizations.storage,
                        disks: { ...prev.optimizations.storage.disks, [index]: updatedDisks[index].capacity },
                    },
                },
            }));
        }
    };

    const handleAddDisk = () => {
        const newDisk = {
            name: disks.length,
            capacity: getMiBFromGiB(DEFAULT_NEW_DISK_SIZE_GB),
        };
        setAdditionalOptimizations((prev) => ({
            ...prev,
            optimizations: {
                ...prev.optimizations,
                storage: {
                    ...prev.optimizations.storage,
                    disks: { ...prev.optimizations.storage.disks, [newDisk.name]: newDisk.capacity },
                },
            },
        }));
        setDisks([...disks, newDisk]);
        scrollToBottom();
    };

    const handleIgnoreOptimizations = (e) => {
        const isChecked = e.target.checked;

        // Update optimizations state, or if unchecked, reset to record's ignored optimizations
        setIgnoredOptimizations((prev) => ({
            ...prev,
            storage: isChecked
                ? [...new Set([...prev.storage, OPTIMIZATION_TYPES.UNDERSIZED, OPTIMIZATION_TYPES.OVERSIZED])]
                : prev.storage.filter((opt) => !REQUIRED_OPTIONS.includes(opt)),
        }));

        scrollToBottom();
    };

    return (
        <div className="storage-settings">
            <section className="settings-subsection">
                <Title level={5}>Current Storage</Title>

                <StorageCriteria
                    criteria={criteria}
                    total={totalSpace}
                    used={totalUsed}
                    disks={record?.storage_disks}
                    disksUsed={record?.storage_disks_used}
                    storageUtilization={record?.storage_utilization}
                />
            </section>

            <section className="settings-subsection">
                <Checkbox
                    checked={isIgnoreOptimizationsChecked}
                    onChange={handleIgnoreOptimizations}
                    disabled={isLoading}
                >
                    Ignore Optimizations
                </Checkbox>
                {/* <Paragraph className="recommendations-text">
                    Recommendations based on usage or below settings.
                </Paragraph> */}
            </section>

            <section className="settings-subsection">
                <Title level={5}>Optimized Storage</Title>
                <Text className="bold">Change Sizes</Text>
                <div className="disk-size-options">
                    <Text className="disk-sizes-label">New Disks:</Text>
                    {disks?.map((disk, index) => (
                        <div key={index} className="disk-size-row">
                            <Text className="disk-label">{`Hard disk: ${index}`}</Text>
                            <div className="disk-input-wrapper">
                                <Input
                                    type="number"
                                    value={getGiBfromMiB(disk.capacity)}
                                    onChange={(e) => handleDiskSizeChange(index, parseInt(e.target.value) || 0)}
                                    onBlur={() => handleDiskSizeBlur(index)}
                                    className="disk-size-input"
                                    disabled={isIgnoreOptimizationsChecked || isLoading}
                                />
                                <span className="size-unit">GB</span>
                                {index >= originalDiskCount && (
                                    <DeleteOutlined onClick={() => handleDeleteDisk(index)} />
                                )}
                            </div>
                        </div>
                    ))}
                    <Button
                        className="add-disk-button"
                        disabled={isIgnoreOptimizationsChecked || isLoading}
                        type="link"
                        icon={<PlusOutlined />}
                        onClick={handleAddDisk}
                    >
                        Disk
                    </Button>
                </div>
            </section>
        </div>
    );
};
